import { Popup } from "devextreme-react";
import { Button } from "devextreme-react/button";
import { CheckBox } from "devextreme-react/check-box";
import { DateBox } from "devextreme-react/date-box";
import { memo, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

type Target = {
  employeeId: number;
  companyId: number;
  name: string;
  surname: string;
} | null;

type Props = {
  visible: boolean;
  isMobile: boolean;
  target: Target;
  submitting: boolean;
  onClose: () => void;
  onSubmit: (payload: {
    startDate: Date;
    endDate: Date;
    indefinite: boolean;
  }) => Promise<void>;
};

export const ShareEmployeePopup2 = memo(function ShareEmployeePopup2({
  visible,
  isMobile,
  target,
  submitting,
  onClose,
  onSubmit,
}: Props) {
  const { t } = useTranslation();
  const [startDate, setStartDate] = useState<Date>(new Date());
  const [endDate, setEndDate] = useState<Date>(() => {
    const d = new Date();
    d.setDate(d.getDate() + 7);
    return d;
  });
  const [indefinite, setIndefinite] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (visible) {
      const today = new Date();
      const next = new Date();
      next.setDate(today.getDate() + 7);
      setStartDate(today);
      setEndDate(next);
      setIndefinite(false);
      setError(null);
    }
  }, [visible]);

  const handleSubmit = async () => {
    if (!indefinite && endDate < startDate) {
      setError(t("transferOfEmployees2.endBeforeStart"));
      return;
    }
    setError(null);
    await onSubmit({ startDate, endDate, indefinite });
  };

  return (
    <Popup
      visible={visible}
      onHiding={onClose}
      fullScreen={isMobile}
      maxWidth={isMobile ? undefined : 480}
      height={isMobile ? "100%" : "auto"}
      maxHeight={isMobile ? undefined : "85vh"}
      title={t("transferOfEmployees2.shareTitle")}
      showCloseButton
      dragEnabled={!isMobile}
    >
      <div className="transfer2-form">
        <p className="transfer2-form-hint">
          {t("transferOfEmployees2.shareHint")}
        </p>
        {target && (
          <div className="transfer2-form-target">
            <div className="transfer2-form-target-label">
              {t("transferOfEmployees2.employee")}
            </div>
            <div className="transfer2-form-target-value">
              {target.surname} {target.name}
            </div>
          </div>
        )}
        <div className="transfer2-form-row">
          <label className="transfer2-form-label" htmlFor="share-start">
            {t("transferOfEmployees2.releaseStart")}
          </label>
          <DateBox
            id="share-start"
            value={startDate}
            type="datetime"
            onValueChanged={(e) => e.value && setStartDate(new Date(e.value))}
            width="100%"
          />
        </div>
        <div className="transfer2-form-row">
          <label className="transfer2-form-label" htmlFor="share-end">
            {t("transferOfEmployees2.releaseEnd")}
          </label>
          <DateBox
            id="share-end"
            value={endDate}
            type="datetime"
            disabled={indefinite}
            min={startDate}
            onValueChanged={(e) => e.value && setEndDate(new Date(e.value))}
            width="100%"
          />
        </div>
        <div className="transfer2-form-row">
          <CheckBox
            value={indefinite}
            text={t("transferOfEmployees2.indefinite")}
            onValueChanged={(e) => setIndefinite(!!e.value)}
          />
          <div className="transfer2-form-help">
            {t("transferOfEmployees2.indefiniteHint")}
          </div>
        </div>
        {error && <div className="transfer2-form-error">{error}</div>}
        <div className="transfer2-form-actions">
          <Button
            text={t("common.cancel")}
            onClick={onClose}
            stylingMode="outlined"
            width={isMobile ? "100%" : undefined}
            disabled={submitting}
          />
          <Button
            text={t("transferOfEmployees2.shareConfirm")}
            type="default"
            stylingMode="contained"
            onClick={handleSubmit}
            width={isMobile ? "100%" : undefined}
            disabled={submitting}
          />
        </div>
      </div>
    </Popup>
  );
});
